import React, { useState } from 'react';
import { Box, ButtonBase, makeStyles } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, MuteIcon, UnMuteIcon } from '../../../components/Icon';
import { UserInfo } from '../../../components/Index/Header';
import { ReactComponent as SwitchOn } from '../../../static/images/SwitchOn.svg';
import { ReactComponent as SwitchOff } from '../../../static/images/SwitchOff.svg';
import { useActionableSettings, useAutoLogout } from '../../../utils/hooks';
import StyleButton from '../../../components/StyleButton';
import { useQuery } from '../../../utils/useQuery';
import RefreshButton from '../../../components/RefreshButton';
import { classNames } from '../../../utils';

const createSettingUpdater = (setActionableSetting) => (type) => () => {
  setActionableSetting((preData) => {
    const newData = {
      ...preData,
      [type]: !preData[type],
    };
    localStorage.setItem('ACTIONABLE_SETTING', JSON.stringify(newData));
    return newData;
  });
};

const ActionableHeader = ({ flash, setRefresh }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const query = useQuery();
  const bedStatusShortCut = location.pathname.includes('actionableDashboardBs');
  const admissionShortCut = location.pathname.includes('actionableDashboardAdm');

  const [actionableSetting, setActionableSetting] = useActionableSettings();

  const handleUpdateSetting = createSettingUpdater(setActionableSetting);

  return (
    <Box
      className={flash && actionableSetting.flashAlert ? classes.flash : ''}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      height="4.5rem"
      width="100vw"
      bgcolor="#FFF"
      boxShadow="0px 1px 0px 0px #E4E7EB"
      p="0.5rem 2.5rem"
    >
      <Box display="flex" gridGap="1rem">
        <ButtonBase onClick={() => history.goBack()}>
          <ArrowLeftIcon className={classes.backIcon} />
          <div className={classes.back}>Back</div>
        </ButtonBase>
        <RefreshButton handleRefresh={() => setRefresh((o) => !o)} />
        {!bedStatusShortCut && !admissionShortCut && (
          <StyleButton
            text="Ack. History"
            type="fill"
            onClick={() =>
              history.push(
                `/actionableDashboard${query.type === 'BS' ? 'Bs' : 'Adm'}?type=${query.type}&ticketType=history`
              )
            }
          />
        )}
        {bedStatusShortCut && (
          <StyleButton text="Bed Status" type="fill" onClick={() => history.push('/newBedstatus')} />
        )}
        {admissionShortCut && <StyleButton text="E-Admission" type="fill" onClick={() => history.push('/admission')} />}
      </Box>
      <Box display="flex" alignItems="center" gridGap="2.5rem">
        <AutoLogoutBtn />
        <ButtonBase className={classes.switchButton} onClick={handleUpdateSetting('flashAlert')}>
          {actionableSetting.flashAlert ? (
            <SwitchOn className={classes.switch} />
          ) : (
            <SwitchOff className={classes.switch} />
          )}
          Flash Alert
        </ButtonBase>
        <MuteBtn />
        <Box display="flex" alignItems="center">
          <UserInfo />
        </Box>
      </Box>
    </Box>
  );
};

export const AutoLogoutBtn = ({ module }) => {
  const classes = useStyles();
  const [actionableSetting, setActionableSetting] = useActionableSettings();
  const handleUpdateSetting = createSettingUpdater(setActionableSetting);
  useAutoLogout(actionableSetting.idleLogout);
  return module === 'bedStatus' ? (
    <ButtonBase
      className={classNames(classes.autoLogoutBtn, actionableSetting.idleLogout && classes.logoutBtnActive)}
      onClick={handleUpdateSetting('idleLogout')}
    >
      1m Idle
      <br />
      Logout
    </ButtonBase>
  ) : (
    <ButtonBase className={classes.eadAutoLogout} onClick={handleUpdateSetting('idleLogout')}>
      1m Idle Logout
      {actionableSetting.idleLogout ? (
        <SwitchOn className={classes.switch} />
      ) : (
        <SwitchOff className={classes.switch} />
      )}
    </ButtonBase>
  );
};

export const MuteBtn = ({ height = '2.938rem', showBorder = true }) => {
  const classes = useStyles({ height, showBorder });
  const [actionableSetting, setActionableSetting] = useActionableSettings();
  const handleUpdateSetting = createSettingUpdater(setActionableSetting);
  return (
    <ButtonBase className={classes.muteBtn} onClick={handleUpdateSetting('mute')}>
      {actionableSetting.mute ? (
        <MuteIcon className={classes.unMuteIcon} />
      ) : (
        <UnMuteIcon className={classes.unMuteIcon} />
      )}
    </ButtonBase>
  );
};

export const BrowserAlertByFilter = () => {
  const classes = useStyles();
  const initBrowserAlertSetting = () => {
    const ADMISSION_BROWSER_ALERT_SETTING = localStorage.getItem('ADMISSION_BROWSER_ALERT_SETTING');
    if (ADMISSION_BROWSER_ALERT_SETTING) {
      return ADMISSION_BROWSER_ALERT_SETTING === 'true';
    }
    localStorage.setItem('ADMISSION_BROWSER_ALERT_SETTING', 'false');
    return false;
  };
  const [browserAlertSetting, setBrowserAlertSetting] = useState(initBrowserAlertSetting);
  const handleUpdateSetting = () => {
    setBrowserAlertSetting((preData) => {
      const newData = !preData;
      localStorage.setItem('ADMISSION_BROWSER_ALERT_SETTING', newData);
      return newData;
    });
  };
  return (
    <ButtonBase className={classes.forMyAction} onClick={handleUpdateSetting}>
      Browser Alert By Specialty Filter
      {browserAlertSetting ? <SwitchOn className={classes.switch} /> : <SwitchOff className={classes.switch} />}
    </ButtonBase>
  );
};

const useStyles = makeStyles(() => ({
  backIcon: {
    fontSize: '1.5rem',
    color: '#538FFF',
  },
  back: {
    fontSize: '1rem',
    color: '#538FFF',
    marginLeft: '1rem',
    fontFamily: 'NotoSans-Bold',
  },
  muteBtn: ({ height, showBorder }) => ({
    color: '#538fff',
    fontSize: '1rem',
    fontFamily: 'NotoSans-Bold',
    border: showBorder ? 'solid 1px #e4e7eb' : '',
    borderRadius: '8px',
    width: showBorder ? '3rem' : '2.26rem',
    height,
    padding: '0 0.563rem',
    display: 'flex',
    columnGap: '0.625rem',
    backgroundColor: '#FFFFFF',
  }),
  unMuteIcon: {
    fontSize: '1.5rem',
  },
  switchButton: {
    fontSize: '0.875rem',
    color: '#7D86A9',
    gap: '0.3rem',
    fontFamily: 'NotoSans-Medium',
  },
  eadAutoLogout: {
    fontSize: '0.75rem',
    color: '#7D86A9',
    gap: '0.3rem',
    fontFamily: 'NotoSans-Medium',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  switch: {
    width: '2.75rem',
    height: '1.47rem',
  },
  '@keyframes backgroundAnimation': {
    '0%': { backgroundColor: '#FFFFFF' },
    '50%': { backgroundColor: 'black' },
    '100%': { backgroundColor: '#FFFFFF' },
  },
  flash: {
    animation: '$backgroundAnimation 2s infinite',
  },
  forMyAction: {
    backgroundColor: '#FDFEFF',
    fontSize: '0.875rem',
    color: '#7D86A9',
    gap: '0.3rem',
    fontFamily: 'NotoSans-Medium',
  },
  autoLogoutBtn: {
    padding: '0.571rem 0.285rem',
    border: '1px solid #E4E7EB',
    background: '#BAD2FF',
    color: '#FFFFFF',
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: '1.1',
    borderRadius: '8px',
  },
  logoutBtnActive: {
    background: '#538FFF',
  },
}));

export default ActionableHeader;
